import { useState } from 'react'
import {
  Search,
  Plus,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Download,
  AlertTriangle,
  Smartphone,
  Code,
  Bug,
  MessageSquare,
} from 'lucide-react'

export default function ITILHelpDeskPage() {
  const [activeTab, setActiveTab] = useState('apps')
  const [filterStatus, setFilterStatus] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewTicketModal, setShowNewTicketModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)

  // Apps data structure
  const apps = [
    {
      id: 'guardbot-core',
      name: 'GuardBot',
      type: 'Security',
      platforms: ['Mac OS', 'iOS', 'Fire TV', 'Fire OS', 'Vega OS', 'Android', 'Windows 10', 'Windows 11', 'Chrome OS', 'Ubuntu CLI', 'Debian'],
      stores: ['Amazon App Store', 'Microsoft Store', 'Meta Horizon', 'Apple App Store', 'Google Play Store', 'Snapcraft', 'Cybertection App Store'],
      tickets: 12,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-15',
    },
    {
      id: 'mailbot',
      name: 'MailBot',
      type: 'Email Management',
      platforms: ['Windows 10', 'Windows 11', 'Mac OS', 'Web'],
      stores: ['Microsoft Store', 'Apple App Store', 'Cybertection App Store'],
      tickets: 5,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-14',
    },
    {
      id: 'web-test-tool',
      name: 'Web App Test Tool',
      type: 'Testing',
      platforms: ['Web', 'Windows', 'Mac OS', 'Linux'],
      stores: ['Cybertection App Store'],
      tickets: 8,
      status: 'operational',
      incidents: 1,
      lastUpdate: '2024-04-13',
    },
    {
      id: 'guardbot-ai-live',
      name: 'GuardBot AI Live',
      type: 'AI Security',
      platforms: ['Web', 'iOS', 'Android', 'Windows'],
      stores: ['Apple App Store', 'Google Play Store', 'Microsoft Store'],
      tickets: 15,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-16',
    },
    {
      id: 'cve-tool',
      name: 'CVE Tool',
      type: 'Vulnerability Management',
      platforms: ['Web', 'CLI', 'Linux', 'Windows'],
      stores: ['Cybertection App Store', 'Snapcraft'],
      tickets: 6,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-12',
    },
    {
      id: 'malware-removal',
      name: 'Malware Removal Tool',
      type: 'Security',
      platforms: ['Windows 10', 'Windows 11', 'Mac OS'],
      stores: ['Microsoft Store', 'Apple App Store', 'Cybertection App Store'],
      tickets: 9,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-11',
    },
  ]

  // Services data structure
  const services = [
    {
      id: 'website-dev',
      name: 'Website Development',
      type: 'Web Services',
      description: 'Custom website development and design',
      activeProjects: 8,
      tickets: 14,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-16',
    },
    {
      id: 'android-dev',
      name: 'Android App Development',
      type: 'Mobile Services',
      description: 'Native and cross-platform Android apps',
      activeProjects: 5,
      tickets: 11,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-15',
    },
    {
      id: 'windows-dev',
      name: 'Windows App Development',
      type: 'Desktop Services',
      description: 'Windows desktop application development',
      activeProjects: 4,
      tickets: 7,
      status: 'operational',
      incidents: 1,
      lastUpdate: '2024-04-14',
    },
    {
      id: 'apple-dev',
      name: 'Apple App Development',
      type: 'Mobile Services',
      description: 'iOS, macOS, and tvOS app development',
      activeProjects: 6,
      tickets: 12,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-16',
    },
    {
      id: 'linux-dev',
      name: 'Linux App Development',
      type: 'Desktop Services',
      description: 'Linux application development and optimization',
      activeProjects: 3,
      tickets: 5,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-13',
    },
    {
      id: 'fire-os-dev',
      name: 'Fire OS App Development',
      type: 'Streaming Services',
      description: 'Amazon Fire OS and Fire TV app development',
      activeProjects: 2,
      tickets: 4,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-12',
    },
    {
      id: 'meta-horizon-dev',
      name: 'Meta Horizon App Development',
      type: 'VR/Metaverse Services',
      description: 'Meta Quest and Horizon platform development',
      activeProjects: 3,
      tickets: 8,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-11',
    },
    {
      id: 'meta-worlds-dev',
      name: 'Meta Worlds App Development',
      type: 'Metaverse Services',
      description: 'Meta Worlds and 3D experience development',
      activeProjects: 2,
      tickets: 3,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-10',
    },
    {
      id: 'api-dev',
      name: 'API Development',
      type: 'Backend Services',
      description: 'REST, GraphQL, and WebSocket API development',
      activeProjects: 7,
      tickets: 16,
      status: 'operational',
      incidents: 0,
      lastUpdate: '2024-04-15',
    },
  ]

  const filteredApps = apps.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    (filterStatus === 'all' || app.status === filterStatus)
  )

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    (filterStatus === 'all' || service.status === filterStatus)
  )

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />
      case 'down':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      default:
        return <Clock className="w-5 h-5 text-blue-300" />
    }
  }

  const getIncidentBadge = (incidents) => {
    if (incidents === 0) return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">No Issues</span>
    if (incidents === 1) return <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">1 Issue</span>
    return <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">{incidents} Issues</span>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">🔧 ITIL Help Desk</h1>
          <p className="text-blue-200 mt-1">Support for Cybertection Apps & Development Services</p>
        </div>
        <button
          onClick={() => setShowNewTicketModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors"
        >
          <Plus size={20} />
          <span>New Ticket</span>
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/30">
          <div className="text-2xl font-bold text-cyber-600">55</div>
          <div className="text-sm text-blue-200">Active Tickets</div>
        </div>
        <div className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/30">
          <div className="text-2xl font-bold text-green-600">15</div>
          <div className="text-sm text-blue-200">Items (Operational)</div>
        </div>
        <div className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/30">
          <div className="text-2xl font-bold text-yellow-600">2</div>
          <div className="text-sm text-blue-200">Items (Degraded)</div>
        </div>
        <div className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/30">
          <div className="text-2xl font-bold text-blue-200">4.2h</div>
          <div className="text-sm text-blue-200">Avg Response Time</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 border-b border-blue-500/30">
        <button
          onClick={() => setActiveTab('apps')}
          className={`flex items-center space-x-2 px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'apps'
              ? 'border-cyber-600 text-cyber-600'
              : 'border-transparent text-blue-200 hover:text-white'
          }`}
        >
          <Smartphone size={20} />
          <span>Apps ({apps.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`flex items-center space-x-2 px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-cyber-600 text-cyber-600'
              : 'border-transparent text-blue-200 hover:text-white'
          }`}
        >
          <Code size={20} />
          <span>Services ({services.length})</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-blue-300" size={20} />
          <input
            type="text"
            placeholder="Search apps or services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-blue-500/40 rounded-lg bg-slate-800/80 text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-blue-500/40 rounded-lg bg-slate-800/80 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all" style={{backgroundColor: '#1e293b', color: '#fff'}}>All Status</option>
          <option value="operational" style={{backgroundColor: '#1e293b', color: '#fff'}}>Operational</option>
          <option value="degraded" style={{backgroundColor: '#1e293b', color: '#fff'}}>Degraded</option>
          <option value="down" style={{backgroundColor: '#1e293b', color: '#fff'}}>Down</option>
        </select>
      </div>

      {/* Apps Tab */}
      {activeTab === 'apps' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {filteredApps.map(app => (
              <div key={app.id} className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-white">{app.name}</h3>
                        {getStatusIcon(app.status)}
                        {getIncidentBadge(app.incidents)}
                      </div>
                      <p className="text-sm text-blue-200 mt-1">{app.type}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedItem(app)
                      setShowNewTicketModal(true)
                    }}
                    className="px-3 py-2 text-sm bg-blue-500/30 text-blue-200 rounded-lg hover:bg-blue-500/40 transition-colors"
                  >
                    Create Ticket
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Active Tickets</div>
                    <div className="text-2xl font-bold text-white">{app.tickets}</div>
                  </div>
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Status</div>
                    <div className="text-lg font-semibold text-green-600 capitalize">{app.status}</div>
                  </div>
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Last Update</div>
                    <div className="text-lg font-semibold text-white">{app.lastUpdate}</div>
                  </div>
                </div>

                <div className="border-t border-blue-500/30 pt-4">
                  <div className="text-xs font-semibold text-blue-200 uppercase mb-2">Platforms & Stores</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {app.platforms.map(platform => (
                      <span key={platform} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
                        {platform}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {app.stores.map(store => (
                      <span key={store} className="text-xs px-2 py-1 bg-purple-500/20 text-purple-200 rounded">
                        {store}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {filteredServices.map(service => (
              <div key={service.id} className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Code className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-white">{service.name}</h3>
                        {getStatusIcon(service.status)}
                        {getIncidentBadge(service.incidents)}
                      </div>
                      <p className="text-sm text-blue-200 mt-1">{service.type}</p>
                      <p className="text-sm text-blue-300/70 mt-1">{service.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedItem(service)
                      setShowNewTicketModal(true)
                    }}
                    className="px-3 py-2 text-sm bg-purple-500/30 text-purple-200 rounded-lg hover:bg-purple-500/40 transition-colors"
                  >
                    Create Ticket
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Active Projects</div>
                    <div className="text-2xl font-bold text-white">{service.activeProjects}</div>
                  </div>
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Tickets</div>
                    <div className="text-2xl font-bold text-white">{service.tickets}</div>
                  </div>
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Status</div>
                    <div className="text-lg font-semibold text-green-600 capitalize">{service.status}</div>
                  </div>
                  <div>
                    <div className="text-xs text-blue-300/70 uppercase tracking-wide">Last Update</div>
                    <div className="text-lg font-semibold text-white">{service.lastUpdate}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800/50 rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Create New Ticket</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Category
                </label>
                <div className="flex space-x-4">
                  <button className="flex-1 px-4 py-2 border-2 border-cyber-600 text-cyber-600 rounded-lg font-medium">
                    {activeTab === 'apps' ? 'App' : 'Service'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Item
                </label>
                <input
                  type="text"
                  placeholder={selectedItem ? selectedItem.name : 'Select an item...'}
                  disabled
                  className="w-full px-4 py-2 border border-blue-500/40 rounded-lg bg-slate-800/80 text-white placeholder-blue-300/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Ticket Type
                </label>
                <select className="w-full px-4 py-2 border border-blue-500/40 rounded-lg bg-slate-800/80 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Incident (Unplanned Outage)</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Service Request</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Problem (Root Cause)</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Change Request</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Bug Report</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Feature Request</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Priority
                </label>
                <select className="w-full px-4 py-2 border border-blue-500/40 rounded-lg bg-slate-800/80 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Critical (P1)</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>High (P2)</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Medium (P3)</option>
                  <option style={{backgroundColor: '#1e293b', color: '#fff'}}>Low (P4)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Describe the issue, request, or problem..."
                  rows={4}
                  className="w-full px-4 py-2 border border-blue-500/40 rounded-lg bg-slate-800/80 text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowNewTicketModal(false)}
                  className="flex-1 px-4 py-2 border border-blue-500/40 text-blue-200 rounded-lg hover:bg-slate-700/30 transition-colors"
                >
                  Cancel
                </button>
                <button className="flex-1 px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors">
                  Create Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
